import React, { useState, useEffect, useRef } from 'react';
import util from '../../styles/utilities.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetCredentialingDashboardQuery } from '../../slices/credentialingApi';
import useAutoCollectBadges from '../../hooks/useAutoCollectBadges';
import { isBadgeReadyForAutoCollection } from '../../utils/credentialingUtils';
import BadgeDetailsModal from '../badges/BadgeDetailsModal';
import styles from './CredentialingDashboard.module.css';
import BadgeProgressCard from './BadgeProgressCard';

// Single Source of Truth: Use backend-calculated progress values only
const getOverallBadgeProgress = (badgeData) => {
  // Backend provides all calculated progress values
  const percentage = Math.round(badgeData.progress.overall_progress_percentage || 0);
  const text = badgeData.progress.overall_status_text || 'Calculating progress...';
  
  return { percentage, text };
};

const getUsageProgress = (badgeData) => {
  // Find the usage component from the backend-calculated components
  const components = badgeData.progress.components || [];
  const usageComponent = components.find(component => component.type === 'usage');
  
  if (usageComponent && usageComponent.progress) {
    return {
      percentage: Math.round(usageComponent.progress.progress_percentage || 0),
      text: usageComponent.progress.status_text || 'No usage progress'
    };
  }
  
  // Fallback to overall progress if usage component not found
  return {
    percentage: Math.round(badgeData.progress.overall_progress_percentage || 0),
    text: badgeData.progress.overall_status_text || 'No usage progress'
  };
};

// Helper function to extract module progress information
const getModuleProgressMap = (badgeData) => {
  const progressMap = {};

  const components = badgeData?.progress?.components || [];
  const modulesComponent = components.find((component) => component.type === 'modules');

  if (modulesComponent?.progress?.modules) {
    modulesComponent.progress.modules.forEach((moduleProgressEntry) => {
      const moduleId = moduleProgressEntry.module_id ?? moduleProgressEntry.moduleId ?? moduleProgressEntry.id;
      if (!moduleId) {
        return;
      }

      progressMap[moduleId] = {
        quiz_status: moduleProgressEntry.quiz_status,
        quiz_attempts: moduleProgressEntry.quiz_attempts,
        quiz_best_score: moduleProgressEntry.quiz_best_score,
        quiz_passed: moduleProgressEntry.quiz_passed,
        is_completed: moduleProgressEntry.is_completed ?? moduleProgressEntry.module_completed,
        progress_percentage: moduleProgressEntry.progress_percentage ?? moduleProgressEntry.module_progress_percentage,
        video_progress_percentage: moduleProgressEntry.video_progress_percentage,
        video_completed: moduleProgressEntry.video_completed,
        last_video_position: moduleProgressEntry.last_video_position,
        content_progress_percentage: moduleProgressEntry.content_progress_percentage,
        content_completed: moduleProgressEntry.content_completed,
        reflection_submitted_at: moduleProgressEntry.reflection_submitted_at,
        usage_requirement: moduleProgressEntry.usage_requirement,
        started_at: moduleProgressEntry.started_at,
        completed_at: moduleProgressEntry.completed_at,
        completion_method: moduleProgressEntry.completion_method,
      };
    });
  }

  // Ensure we still have a progress entry for each module and fall back to module metadata when needed
  if (badgeData.modules) {
    badgeData.modules.forEach((module) => {
      const existing = progressMap[module.id] || {};
      progressMap[module.id] = {
        quiz_status: existing.quiz_status ?? module.quiz_status,
        quiz_attempts: existing.quiz_attempts ?? module.quiz_attempts,
        quiz_best_score: existing.quiz_best_score ?? module.quiz_best_score,
        quiz_passed: existing.quiz_passed ?? module.quiz_passed,
        is_completed: existing.is_completed ?? module.is_completed,
        progress_percentage: existing.progress_percentage ?? module.progress_percentage,
        video_progress_percentage: existing.video_progress_percentage ?? module.video_progress_percentage,
        video_completed: existing.video_completed ?? module.video_completed,
        last_video_position: existing.last_video_position ?? module.last_video_position,
        content_progress_percentage: existing.content_progress_percentage ?? module.content_progress_percentage,
        content_completed: existing.content_completed ?? module.content_completed,
        reflection_submitted_at: existing.reflection_submitted_at ?? module.reflection_submitted_at,
        usage_requirement: existing.usage_requirement ?? module.usage_requirement,
        started_at: existing.started_at ?? module.started_at,
        completed_at: existing.completed_at ?? module.completed,
        completion_method: existing.completion_method ?? module.completion_method,
      };
    });
  }

  return progressMap;
};

const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

const CredentialingDashboard = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { 
    data: dashboardData, 
    isLoading, 
    error, 
    refetch 
  } = useGetCredentialingDashboardQuery();
  
  // Skeleton loading UX: keep a shimmer placeholder for at least 500ms to avoid jarring swaps
  const ENABLE_MIN_LOADING_DELAY = process.env.NODE_ENV !== 'test';
  const MIN_SKELETON_MS = 250;
  const [showSkeleton, setShowSkeleton] = useState(process.env.NODE_ENV !== 'test');
  const skeletonStartRef = useRef(Date.now());
  const pendingHideRef = useRef(null);

  // Control skeleton visibility based on data loading and minimum duration
  useEffect(() => {
    // When we start loading, ensure skeleton is visible and mark the start time
    if (isLoading) {
      if (!showSkeleton) setShowSkeleton(true);
      // reset start clock for a fresh delay window
      skeletonStartRef.current = Date.now();
      // clear any pending hides
      if (pendingHideRef.current) {
        clearTimeout(pendingHideRef.current);
        pendingHideRef.current = null;
      }
      return;
    }

    // If not loading and we have data, decide when to hide the skeleton
    if (!isLoading && dashboardData) {
      const elapsed = Date.now() - (skeletonStartRef.current || Date.now());
      const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);

      if (!ENABLE_MIN_LOADING_DELAY || remaining === 0) {
        setShowSkeleton(false);
      } else {
        pendingHideRef.current = setTimeout(() => {
          setShowSkeleton(false);
          pendingHideRef.current = null;
        }, remaining);
      }
    }

    return () => {
      if (pendingHideRef.current) {
        clearTimeout(pendingHideRef.current);
        pendingHideRef.current = null;
      }
    };
  }, [isLoading, dashboardData, ENABLE_MIN_LOADING_DELAY]);
  
  // Debug: log the full raw dashboard payload (including all badge data) once when it first arrives
  const hasLoggedDashboardData = useRef(false);
  useEffect(() => {
    if (dashboardData && !hasLoggedDashboardData.current && process.env.NODE_ENV !== 'test') {
      try {
        // Use structuredClone fallback for deep cloning to avoid mutations in console tools
        const cloned = typeof structuredClone === 'function' 
          ? structuredClone(dashboardData) 
          : JSON.parse(JSON.stringify(dashboardData));
        // Single comprehensive log of entire payload
        console.log('[CredentialingDashboard] Full dashboardData payload:', cloned);
        // Additionally log each badge entry for quicker scanning
        const badgeList = dashboardData?.data?.badges;
        if (Array.isArray(badgeList)) {
          badgeList.forEach((badgeObj, idx) => {
            console.log('[CredentialingDashboard] Badge[' + idx + '] raw:', badgeObj);
          });
        }
      } catch (e) {
        console.warn('[CredentialingDashboard] Failed to log dashboardData', e, dashboardData);
      } finally {
        hasLoggedDashboardData.current = true; // prevent re-logging on minor reactive changes
      }
    }
  }, [dashboardData]);
  
  // Badge mutations only - data comes from dashboard
  // Scroll position management
  const SCROLL_STORAGE_KEY = 'credentialing-dashboard-scroll';

  // Disable browser's automatic scroll restoration to prevent conflicts
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Restore scroll position immediately after data loads
  useEffect(() => {
    if (!isLoading && dashboardData) {
      const savedScrollPosition = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      if (savedScrollPosition) {
        // Use requestAnimationFrame for smooth, immediate restoration
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
          // Clear the stored position after restoration
          sessionStorage.removeItem(SCROLL_STORAGE_KEY);
        });
      }
    }
  }, [isLoading, dashboardData]);
  
  // Save scroll position before navigation
  const saveScrollPosition = () => {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, window.scrollY.toString());
  };

  const badges = dashboardData?.data?.badges || [];

  const autoCollectStatuses = useAutoCollectBadges(badges);

  
  // Badge modal state
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [selectedDigitalBadge, setSelectedDigitalBadge] = useState(null);
  const [highlightBadgeId, setHighlightBadgeId] = useState(null);
  const badgeRefs = useRef({});
  const focusHandledRef = useRef(false);

  useEffect(() => {
    const focusId = routerLocation.state?.focusBadgeId;
    if (!focusId || !badges.length || focusHandledRef.current || showSkeleton) {
      return;
    }

    const refs = badgeRefs.current[focusId];
    if (refs?.card) {
      refs.card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightBadgeId(focusId);
      focusHandledRef.current = true;
      navigate('.', { replace: true, state: {} });
    }
  }, [badges, routerLocation.state, navigate, showSkeleton]);

  useEffect(() => {
    if (!highlightBadgeId) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightBadgeId(null);
      focusHandledRef.current = false;
    }, 5200);

    return () => window.clearTimeout(timer);
  }, [highlightBadgeId]);

  const handleModuleClick = (moduleId, moduleSlug, section = null) => {
    // Save current scroll position before navigating
    saveScrollPosition();
    
    // Build URL with section parameter if specified
    let url = `/credentialing/modules/${moduleId}`;
    if (section) {
      url += `?section=${section}`;
    }
    
    navigate(url, { 
      state: { moduleSlug } 
    });
  };


  // Badge handling functions  
  const handleViewBadge = async (badgeData) => {
    console.log('handleViewBadge called with data:', badgeData);
    console.log('Progress data:', badgeData.progress);
    console.log('credential_id:', badgeData.progress.credential_id);
    console.log('verification_url:', badgeData.progress.verification_url);
    
    if (badgeData.progress.is_earned) {
      // Create earned badge data from dashboard data
      const earnedBadge = {
        badge_id: badgeData.badge.id,
        name: badgeData.badge.name,
        badge_type: badgeData.badge.badge_type,
        description: badgeData.badge.description,
        credential_id: badgeData.progress.credential_id,
        verification_url: badgeData.progress.verification_url,
        issued_date: badgeData.progress.earned_at,
        earned_at: badgeData.progress.earned_at,
        view_count: 0,
        linkedin_shared: false,
        linkedin_shared_date: null,
        is_revoked: false
      };
      
      console.log('Created earnedBadge object:', earnedBadge);
      
      setSelectedBadge(badgeData.badge);
      setSelectedDigitalBadge(earnedBadge);
    } else {
      console.error('Trying to view badge that is not earned');
      alert('Badge has not been earned yet.');
    }
  };

  const handleLinkedInShare = (shareData) => {
    // Open LinkedIn sharing in new window
    if (shareData.sharing_data?.direct_share_url) {
      window.open(shareData.sharing_data.direct_share_url, '_blank');
    }
  };

  const handleDownloadJSON = (digitalBadge) => {
    console.log('Badge JSON downloaded:', digitalBadge.credential_id);
  };

  // Removed manual tooltip state in favor of MUI Tooltip component

  // Note: we no longer early-return on isLoading so we can render a skeleton that preserves layout.

  if (error) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.statePanel}>
          <h2 className={styles.errorTitle}>Unable to Load Training Dashboard</h2>
          <p>There was an error loading your training progress.</p>
          <button 
            className={`${util.btn} ${util['btn--primaryGradient']}`}
            onClick={() => refetch()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className={styles.dashboard} data-testid="credentialing-dashboard">
      <div className={styles.header}>
        <h1 className={styles.title}>Let's get you certified!</h1>
      </div>

      <div className={styles.progressList} aria-busy={showSkeleton ? 'true' : undefined}>
        {showSkeleton && (
          Array.from({ length: badges.length > 0 ? badges.length : 2 }).map((_ , idx) => (
            <BadgeProgressCard key={`skeleton-${idx}`} skeleton />
          ))
        )}
        {!showSkeleton && badges.map((badgeData) => {
          const badgeId = badgeData.badge.id;
          const autoStatus = autoCollectStatuses[badgeId];
          const isHighlighted = highlightBadgeId === badgeId;
          const overallProgress = getOverallBadgeProgress(badgeData);
          const moduleProgress = getModuleProgressMap(badgeData);
          return (
            <BadgeProgressCard
              key={badgeId}
              badgeData={badgeData}
              overallProgress={overallProgress}
              moduleProgress={moduleProgress}
              autoStatus={autoStatus}
              highlight={isHighlighted}
              onViewBadge={handleViewBadge}
              onModuleClick={handleModuleClick}
              onRegisterRefs={({ card, shareButton }) => {
                if (!badgeId) return;
                badgeRefs.current[badgeId] = {
                  card,
                  shareButton,
                };
              }}
            />
          );
        })}
      </div>
      {badges.some((badge) => !badge.progress.is_earned) && (
        <div className={styles.encouragement}>
          <div className={styles.encouragementCard}>
            {badges.some((badge) => badge.progress.completed_modules > 0) && (
              <p>
                <strong>You're making great progress!</strong>
              </p>
            )}
            <p>
              Thank you for making a real difference in the lives of your students.
            </p>
          </div>
        </div>
      )}
      <BadgeDetailsModal
        badge={selectedBadge}
        digitalBadge={selectedDigitalBadge}
        isOpen={!!selectedBadge}
        isLoading={false}
        onClose={() => {
          setSelectedBadge(null);
          setSelectedDigitalBadge(null);
        }}
        onLinkedInShare={handleLinkedInShare}
        onDownloadJSON={handleDownloadJSON}
      />
    </section>
  );
};

export default CredentialingDashboard;
